import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getProjectCrew } from '../services/crewService';

// Provides `can(key)`, `permissions`, `isOwner`, `isAdmin` for the active project.
// Expect parent to provide projectId and currentUserId.
const PermissionsContext = createContext(null);

export function PermissionsProvider({ projectId, currentUserId, children }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [member, setMember] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!projectId || !currentUserId) return;
      setLoading(true);
      setError(null);
      try {
        const crew = await getProjectCrew(projectId, { force: true });
        if (!mounted) return;
        const me = (crew || []).find(c => c.user?._id === currentUserId || c.user?._id?._id === currentUserId || c.userId === currentUserId);
        setMember(me || null);
      } catch (e) {
        if (!mounted) return;
        setError(e);
        setMember(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [projectId, currentUserId]);

  const value = useMemo(() => {
    const isOwner = !!member?.isOwner;
    const isAdmin = !!member?.isAdmin || isOwner;
    const permissions = member?.permissions || {};
    
    const can = (key) => {
      if (isOwner) return true;
      if (isAdmin) return true; // admins have full access by template today
      if (!key) return false;
      if (!permissions || typeof permissions !== 'object') return false;

      if (Object.prototype.hasOwnProperty.call(permissions, key)) {
        return permissions[key] === true;
      }

      if (key.includes('.')) {
        const parts = key.split('.');
        let cur = permissions;
        for (const p of parts) {
          if (!cur || typeof cur !== 'object' || !(p in cur)) return false;
          cur = cur[p];
        }
        return cur === true;
      }

      return false;
    };
    return { loading, error, member, isOwner, isAdmin, permissions, can };
  }, [member, loading, error]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error('usePermissions must be used within a PermissionsProvider');
  return ctx;
}
